// Reusable task card component for displaying task items with status and actions

import {
  Card,
  CardBody,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  Icon,
  Box,
  Tooltip,
  Progress,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdCheckCircle, MdLock, MdPending, MdEdit, MdPlayArrow, MdHistory, MdSync, MdSchedule, MdWarning } from 'react-icons/md';
import { ReactNode } from 'react';

export type TaskCardStatus = 'completed' | 'blocked' | 'pending' | 'wip' | 'due_soon' | 'soft_overdue';

export interface TaskCardProps {
  /** Task title/name */
  title: string;
  /** Task number for display (optional) */
  number?: number;
  /** Current status of the task */
  status: TaskCardStatus;
  /** Display value shown for completed tasks */
  displayValue?: string;
  /** Optional progress percentage (0-100) - only shown if provided */
  progress?: number;
  /** Number of times this task has been edited (shows edit history indicator) */
  editCount?: number;
  /** Action button label */
  actionLabel?: string;
  /** Action button tooltip */
  actionTooltip?: string;
  /** Custom action button icon */
  actionIcon?: ReactNode;
  /** Click handler for the card */
  onClick?: () => void;
  /** Click handler for the action button (if different from card click) */
  onActionClick?: () => void;
  /** Whether the card is disabled */
  isDisabled?: boolean;
  /** Soft due date ISO string for urgency display */
  softDueDate?: string | null;
  /** Who last updated the task */
  userName?: string;
  /** ISO timestamp of last update */
  updatedAt?: string;
}

/**
 * Reusable task card component with status indicators and optional progress
 * Used in workflow pages and can be reused for other task-like displays
 */
export function TaskCard({
  title,
  number,
  status,
  displayValue,
  progress,
  editCount,
  actionLabel,
  actionTooltip,
  actionIcon,
  onClick,
  onActionClick,
  isDisabled = false,
  softDueDate,
  userName,
  updatedAt,
}: TaskCardProps) {
  const isCompleted = status === 'completed';
  const isBlocked = status === 'blocked';
  const isPending = status === 'pending';
  const isWip = status === 'wip';
  const isDueSoon = status === 'due_soon';
  const isSoftOverdue = status === 'soft_overdue';

  // Background colors based on status
  const completedBg = useColorModeValue('green.50', 'green.900');
  const pendingBg = useColorModeValue('gray.50', 'gray.700');
  const blockedBg = useColorModeValue('red.50', 'red.900');
  const wipBg = useColorModeValue('orange.50', 'orange.900');
  const dueSoonBg = useColorModeValue('yellow.50', 'yellow.900');
  const softOverdueBg = useColorModeValue('red.50', 'red.900');

  const bgColor = isBlocked ? blockedBg
    : isCompleted ? completedBg
    : isWip ? wipBg
    : isSoftOverdue ? softOverdueBg
    : isDueSoon ? dueSoonBg
    : pendingBg;
  const borderColor = isCompleted ? 'green.200'
    : isBlocked ? 'red.200'
    : isWip ? 'orange.200'
    : isSoftOverdue ? 'red.300'
    : isDueSoon ? 'yellow.200'
    : 'border-default';

  // Status icon
  const StatusIcon = isCompleted ? MdCheckCircle
    : isBlocked ? MdLock
    : isWip ? MdSync
    : isSoftOverdue ? MdWarning
    : isDueSoon ? MdSchedule
    : MdPending;
  const statusIconColor = isCompleted ? 'green.500'
    : isBlocked ? 'red.400'
    : isWip ? 'orange.500'
    : isSoftOverdue ? 'red.500'
    : isDueSoon ? 'yellow.500'
    : 'gray.400';

  // Status badge
  const statusLabel = isCompleted ? 'Completed'
    : isBlocked ? 'Blocked'
    : isWip ? 'In Progress'
    : isSoftOverdue ? 'Overdue'
    : isDueSoon ? 'Due Soon'
    : 'Pending';
  const statusColorScheme = isCompleted ? 'green'
    : isBlocked ? 'red'
    : isWip ? 'orange'
    : isSoftOverdue ? 'red'
    : isDueSoon ? 'yellow'
    : 'gray';

  // Action button defaults
  const defaultActionLabel = isCompleted ? 'Edit' : isWip ? 'Update' : 'Process';
  const defaultActionIcon = isCompleted ? <MdEdit /> : isWip ? <MdEdit /> : <MdPlayArrow />;
  const defaultActionTooltip = isBlocked
    ? 'Waiting for approval'
    : isCompleted
    ? 'Edit response'
    : isWip
    ? 'Add notes or mark complete'
    : 'Enter data';

  // Soft due date deadline line — computed from the prop directly so it renders
  // correctly for WIP tasks and for the ⚠️ "upcoming" tier (> 24h in future).
  const deadlineLine = (() => {
    if (!softDueDate || isCompleted) return null;
    const softDue = new Date(softDueDate);
    const now = new Date();
    const diffMs = softDue.getTime() - now.getTime();
    if (diffMs < 0) {
      return `🔴 Deadline passed ${softDue.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`;
    }
    if (diffMs <= 24 * 60 * 60 * 1000) {
      return `⏰ Due today`;
    }
    return `⚠️ Due ${softDue.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`;
  })();

  const handleCardClick = () => {
    if (!isDisabled && !isBlocked && onClick) {
      onClick();
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onActionClick) {
      onActionClick();
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <Card
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      opacity={isBlocked || isDisabled ? 0.7 : 1}
      cursor={isBlocked || isDisabled ? 'not-allowed' : 'pointer'}
      _hover={
        !isBlocked && !isDisabled
          ? { borderColor: 'brand.500', transform: 'translateY(-1px)' }
          : {}
      }
      transition="all 0.2s"
      onClick={handleCardClick}
    >
      <CardBody py={4} px={5}>
        <VStack spacing={3} align="stretch">
          {/* Title row: icon + full-width title, never truncated */}
          <HStack spacing={3} align="start">
            <Box flexShrink={0} pt={0.5}>
              <Icon as={StatusIcon} color={statusIconColor} boxSize={5} />
            </Box>
            <Text
              fontWeight="semibold"
              color="text-primary"
              fontSize={{ base: 'sm', md: 'md' }}
            >
              {number !== undefined ? `${number}. ` : ''}
              {title}
            </Text>
          </HStack>

          {/* Footer row: badge + deadline on left, action button on right */}
          <HStack justify="space-between" align="center" spacing={3}>
            <VStack align="start" spacing={1} flex={1} minW={0}>
              <HStack spacing={2} flexWrap="wrap">
                <Badge colorScheme={statusColorScheme} fontSize="xs">
                  {statusLabel}
                </Badge>
                {editCount !== undefined && editCount > 0 && (
                  <Tooltip label={`Edited ${editCount} time${editCount > 1 ? 's' : ''}`}>
                    <Badge
                      colorScheme="orange"
                      fontSize="xs"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <Icon as={MdHistory} boxSize={3} />
                      {editCount}
                    </Badge>
                  </Tooltip>
                )}
              </HStack>
              {deadlineLine && (
                <Text
                  fontSize="xs"
                  color={deadlineLine.startsWith('🔴') ? 'red.500' : deadlineLine.startsWith('⏰') ? 'yellow.600' : 'orange.500'}
                  fontWeight="medium"
                  whiteSpace="nowrap"
                >
                  {deadlineLine}
                </Text>
              )}
            </VStack>

            <Box flexShrink={0}>
              <Tooltip label={actionTooltip || defaultActionTooltip}>
                <Button
                  size="sm"
                  colorScheme={isCompleted ? 'gray' : isWip ? 'orange' : isSoftOverdue ? 'red' : isDueSoon ? 'yellow' : 'brand'}
                  variant={isCompleted || isWip ? 'outline' : isSoftOverdue ? 'solid' : isDueSoon ? 'outline' : 'solid'}
                  leftIcon={actionIcon || defaultActionIcon}
                  isDisabled={isBlocked || isDisabled}
                  onClick={handleActionClick}
                >
                  {actionLabel || defaultActionLabel}
                </Button>
              </Tooltip>
            </Box>
          </HStack>

          {/* Display value for tasks with a note */}
          {displayValue && (
            <Text fontSize="sm" color={isBlocked ? 'text-secondary' : 'green.600'} fontWeight="medium">
              {displayValue}
            </Text>
          )}

          {/* User and timestamp metadata */}
          {(userName || updatedAt) && (
            <HStack spacing={2} flexWrap="wrap">
              {userName && (
                <Text fontSize="xs" color="text-secondary">👤 {userName}</Text>
              )}
              {updatedAt && (
                <Text fontSize="xs" color="text-secondary">
                  🕐 {new Date(updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                  {' '}
                  {new Date(updatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </Text>
              )}
            </HStack>
          )}

          {/* Optional progress bar */}
          {progress !== undefined && (
            <Progress
              value={progress}
              size="xs"
              colorScheme={isCompleted ? 'green' : 'brand'}
              borderRadius="full"
            />
          )}
        </VStack>
      </CardBody>
    </Card>
  );
}
